import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { UiButtonComponent, UIIconComponent, UiNumberComponent } from '@design-system';
import { filter, tap } from 'rxjs/operators';
import { MASKED_VALUE } from '../../../../models/masked-value.model';
import { getWalletBuyingPower } from '../../../../models/wallet.model';
import { GeneralSettingsService } from '../../../../services/general-settings.service';
import { WalletStore } from '../../data-access/wallet.store';

/** One line of the detail panel: what it is called, what it is worth, how it is coloured. */
interface BuyingPowerRow {
	label: string;
	value: number;
	/** بلوکه شده is the one figure the handoff colours; the others stay neutral. */
	emphasised: boolean;
	dataCy: string;
}

/**
 * قدرت خرید pinned above the tab bar, on every screen that carries the footer, and
 * the detail it opens into.
 *
 * The detail expands in place — the strip grows upward and the tabs stay put, which
 * is how the handoff draws it. Deliberately not the app's `BottomSheetComponent`:
 * that one portals to `<body>` and rises from the bottom of the viewport over the
 * footer, so the panel would appear to come from somewhere other than the control
 * that opened it.
 *
 * Always the گواهی سپرده wallet: the handoff is explicit that this figure does not
 * follow کیف پیش‌فرض — «آیتم قدرت خرید موجود در نوار پایین سامانه مستقل از انتخاب
 * کیف پیش‌فرض است و همواره قدرت خرید گواهی سپرده کالایی را نمایش می‌دهد».
 *
 * Masked by «عدم نمایش مانده» like every other balance the app prints, and unlike
 * the same figure in the order form — a trader entering an order has to see what
 * they can spend.
 *
 * Prints a figure only once a read has landed. The store opens every wallet at 0, and
 * 0 ریال on a strip that is on screen everywhere is a statement — «your wallet is
 * empty» — not a placeholder, so a wallet nobody has managed to read says so and
 * offers the read again instead. Refreshing it is all this control does in that
 * state: there is no panel worth opening over three zeros.
 */
@Component({
	selector: 'app-wallet-buying-power-bar',
	imports: [UiButtonComponent, UIIconComponent, UiNumberComponent],
	templateUrl: './wallet-buying-power-bar.component.html',
	host: {
		// Holds only the collapsed strip's height (h-8 + border-t): the open panel grows
		// up over the page instead of taking height from it, so nothing pinned to the
		// bottom of the screen above the footer — the buy/sell bar — gets pushed up.
		class: 'relative block h-[calc(2rem+1px)] w-full',
		'(document:keydown.escape)': 'close()'
	}
})
export class WalletBuyingPowerBarComponent {
	private readonly walletStore = inject(WalletStore);
	private readonly router = inject(Router);

	readonly wallet = this.walletStore.depositWallet;
	readonly buyingPower = this.walletStore.depositBuyingPower;
	readonly amountsHidden = inject(GeneralSettingsService).hideBalances;

	/** Whether the detail panel is open. Held here so the strip and the panel agree. */
	readonly expanded = signal(false);

	/** Ties the strip to the region it opens, for `aria-controls`. */
	readonly panelId = 'wallet-buying-power-panel';

	readonly toggleLabel = computed(() => (this.expanded() ? 'بستن جزئیات قدرت خرید' : 'جزئیات قدرت خرید'));

	readonly rows = computed<BuyingPowerRow[]>(() => {
		const wallet = this.wallet();

		return [
			{
				label: 'مانده گواهی سپرده',
				value: wallet.balance,
				emphasised: false,
				dataCy: 'wallet-buying-power-balance'
			},
			{ label: 'بلوکه شده', value: wallet.blocked, emphasised: true, dataCy: 'wallet-buying-power-blocked' },
			{
				label: 'قدرت خرید',
				value: getWalletBuyingPower(wallet),
				emphasised: false,
				dataCy: 'wallet-buying-power-value'
			}
		];
	});

	readonly maskedValue = MASKED_VALUE;

	constructor() {
		// The bar outlives the screen behind it — it is in the footer, not the page — so
		// leaving a tab with the panel open would carry it onto the next screen. Closed
		// on `NavigationStart` rather than `End`, so it is already on its way shut as the
		// new screen paints; `NavigationSkipped` covers tapping the tab already showing.
		toSignal(
			this.router.events.pipe(
				filter(event => event instanceof NavigationStart || event instanceof NavigationSkipped),
				tap(() => this.expanded.set(false))
			)
		);
	}

	/**
	 * The one control, in whichever of its three states it is: the panel's handle once
	 * there is a figure, the retry after a failed read, and inert while a read is out.
	 */
	onToggle(): void {
		this.expanded.update(open => !open);
	}

	close(): void {
		this.expanded.set(false);
	}

	onDepositRequested(): void {
		this.expanded.set(false);
		this.router.navigate(['/wallet/deposit']);
	}
}
